import { useEffect, useRef } from 'react';
import { Flame } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';

/**
 * AgeGateModal - first-time 18+ confirmation before enabling NSFW content.
 *
 * Opens whenever uiStore.pendingAdultRating is set (a user tried to enable
 * NSFW without having confirmed their age on this device). Confirming
 * persists the flag and applies the pending rating; declining leaves the
 * content filter unchanged. Mounted once in App.tsx so it covers every
 * route that can enable NSFW.
 *
 * Deliberately CSS-animated (no framer-motion) so it lives in the entry
 * bundle at ~2kB and opens instantly — a lazy chunk here caused a visible
 * delay on first open.
 */
export function AgeGateModal() {
  const pendingAdultRating = useUIStore((s) => s.pendingAdultRating);
  const confirmAdult = useUIStore((s) => s.confirmAdult);
  const cancelAdultConfirmation = useUIStore((s) => s.cancelAdultConfirmation);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const isOpen = pendingAdultRating !== null;

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    confirmRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelAdultConfirmation();
        return;
      }
      // Trap Tab between the two buttons — background stays inert to keyboard.
      if (e.key === 'Tab') {
        const first = confirmRef.current;
        const last = cancelRef.current;
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (document.activeElement !== first && document.activeElement !== last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [isOpen, cancelAdultConfirmation]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 age-gate-fade-in"
      onClick={cancelAdultConfirmation}
    >
      <div
        className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-xl text-center age-gate-pop-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
        aria-describedby="age-gate-description"
      >
        <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
          <Flame className="w-7 h-7 text-red-400" />
        </div>

        <h2
          id="age-gate-title"
          className="text-2xl font-bold text-[var(--color-text)]"
        >
          Adult Content
        </h2>

        <p
          id="age-gate-description"
          className="text-[var(--color-text-muted)] mt-3"
        >
          This section contains explicit artwork intended for adults only.
          Please confirm that you are 18 years of age or older to continue.
        </p>

        <div className="flex flex-col gap-3 mt-6">
          <button
            ref={confirmRef}
            onClick={confirmAdult}
            className="w-full px-6 py-3 rounded-full bg-[var(--color-primary)] text-[var(--color-bg)] font-semibold hover:bg-[var(--color-primary-light)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-card)]"
          >
            I'm 18 or older — continue
          </button>
          <button
            ref={cancelRef}
            onClick={cancelAdultConfirmation}
            className="w-full px-6 py-3 rounded-full bg-[var(--color-bg-light)] border border-[var(--color-border)] text-[var(--color-text)] font-medium hover:border-[var(--color-primary)]/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-card)]"
          >
            No, keep it safe
          </button>
        </div>

        <p className="text-xs text-[var(--color-text-muted)] mt-4">
          You'll only be asked once on this device.
        </p>
      </div>
    </div>
  );
}
